import { useEffect, useState } from 'react';

import type { ParagraphElement, SuggestionMarkerPair } from '@aline/common';
import {
  isEditWithSource,
  isParagraphElement,
  isSuggestionMarkerStartElement,
  Queries,
} from '@aline/common';
import { equals } from 'ramda';
import type { Range } from 'slate';
import { Editor, Node, Text } from 'slate';
import { useCurrentUserId } from 'src/gql';
import {
  useDocContentChangedCount,
  useMaybeEditor,
  useSuggestionContentChangedCount,
} from 'src/layout-engine';

/**
 * One pending AI suggestion as the toolbar sees it. The sourced suggested
 * edits (grouped by their `source.groupId`) are the suggestion itself; the
 * marker pair is an optional refinement that pins the exact highlight range
 * for in-paragraph edits. Block suggestions (inserted/removed blocks, table
 * rows/columns/tables) carry no marker — their extent is the paragraph span
 * of the nodes carrying the group's edits.
 */
export interface SuggestionUnit {
  endParagraphLocationId: string | null;
  groupId: string;
  /** Marker pair when the suggestion has one (in-paragraph edits). */
  marker: SuggestionMarkerPair | null;
  messageId: string | null;
  reasoning: string | null;
  startParagraphLocationId: string | null;
  threadId: string | null;
}

/**
 * Builds the document-ordered suggestion units for the current user in a
 * single document walk: a unit is created the first time anything belonging
 * to its group is encountered — its marker start element (which precedes the
 * marked text) or its first sourced edit (on text nodes, table rows, table
 * cells, and paragraph pilcrows). First-encounter ordering keeps every unit
 * at its document position, including marker-only units (legacy suggestions
 * whose edits carry no source), so toolbar navigation and the counter walk
 * the array as document order.
 */
export function computeSuggestionUnits(
  editor: Editor,
  currentUserId: string,
  suggestionMarkers: SuggestionMarkerPair[]
): SuggestionUnit[] {
  const markersBySuggestionId = new Map<string, SuggestionMarkerPair>();
  for (const marker of suggestionMarkers) {
    if (!markersBySuggestionId.has(marker.start.suggestionId))
      markersBySuggestionId.set(marker.start.suggestionId, marker);
  }

  const unitsByGroupId = new Map<string, SuggestionUnit>();

  for (const [node, path] of Editor.nodes(editor, { at: [], voids: true })) {
    if (isSuggestionMarkerStartElement(node)) {
      // Only markers in `suggestionMarkers` count: they are pre-filtered by
      // author and guaranteed to have a matching end element.
      const marker = markersBySuggestionId.get(node.suggestionId);
      if (marker && !unitsByGroupId.has(node.suggestionId)) {
        unitsByGroupId.set(node.suggestionId, {
          endParagraphLocationId: null,
          groupId: node.suggestionId,
          marker,
          messageId: null,
          reasoning: marker.start.reasoning || null,
          startParagraphLocationId: null,
          threadId: null,
        });
      }
      continue;
    }

    const edits = Queries.getNodeSuggestedEdits(node).filter(
      edit => isEditWithSource(edit) && edit.authorId === currentUserId
    );
    if (edits.length === 0) continue;

    const locationId = getNodeParagraphLocationId(editor, node, path);
    for (const edit of edits) {
      if (!isEditWithSource(edit)) continue;

      const existing = unitsByGroupId.get(edit.source.groupId);
      if (!existing) {
        unitsByGroupId.set(edit.source.groupId, {
          endParagraphLocationId: locationId,
          groupId: edit.source.groupId,
          marker: markersBySuggestionId.get(edit.source.groupId) ?? null,
          messageId: edit.source.messageId,
          reasoning: edit.reasoning ?? null,
          startParagraphLocationId: locationId,
          threadId: edit.source.threadId,
        });
        continue;
      }

      existing.endParagraphLocationId = locationId ?? existing.endParagraphLocationId;
      existing.startParagraphLocationId = existing.startParagraphLocationId ?? locationId;
      existing.messageId = existing.messageId ?? edit.source.messageId;
      existing.reasoning = existing.reasoning ?? edit.reasoning ?? null;
      existing.threadId = existing.threadId ?? edit.source.threadId;
    }
  }

  return [...unitsByGroupId.values()];
}

/**
 * The unit's document range: the marker's exact range when a pair exists
 * (in-paragraph edits), otherwise the span from the start of its first
 * paragraph to the end of its last.
 */
export function getSuggestionUnitRange(editor: Editor, unit: SuggestionUnit): Range | null {
  if (unit.marker) {
    const markerRange = Queries.getSuggestionRange(editor, unit.groupId);
    if (markerRange) return markerRange;
  }
  if (!unit.startParagraphLocationId) return null;

  const startParagraphEntry = findParagraphByLocationId(editor, unit.startParagraphLocationId);
  if (!startParagraphEntry) return null;

  const range: Range = {
    anchor: Editor.start(editor, startParagraphEntry[1]),
    focus: Editor.end(editor, startParagraphEntry[1]),
  };
  if (
    unit.endParagraphLocationId &&
    unit.endParagraphLocationId !== unit.startParagraphLocationId
  ) {
    const endParagraphEntry = findParagraphByLocationId(editor, unit.endParagraphLocationId);
    if (endParagraphEntry) range.focus = Editor.end(editor, endParagraphEntry[1]);
  }

  return range;
}

/**
 * Computes the suggestion units for the current editor. Returns a stable
 * array reference between content ticks so consumers can use it as an effect
 * dependency without churn.
 */
export function useComputeSuggestionUnits(
  suggestionMarkers: SuggestionMarkerPair[]
): SuggestionUnit[] {
  const editor = useMaybeEditor();
  const currentUserId = useCurrentUserId();
  const { docContentChangedCount } = useDocContentChangedCount();
  const { suggestionContentChangedCount } = useSuggestionContentChangedCount();
  const [suggestionUnits, setSuggestionUnits] = useState<SuggestionUnit[]>([]);

  useEffect(() => {
    if (!docContentChangedCount || !editor || !currentUserId) return;

    const nextUnits = computeSuggestionUnits(editor, currentUserId, suggestionMarkers);
    // Skip the state update when nothing changed so consumers do not re-run expensive effects on every content tick.
    setSuggestionUnits(prev => (equals(prev, nextUnits) ? prev : nextUnits));
  }, [
    editor,
    docContentChangedCount,
    suggestionContentChangedCount,
    currentUserId,
    suggestionMarkers,
  ]);

  return suggestionUnits;
}

/**
 * The locationId of the paragraph a sourced edit belongs to: the containing
 * paragraph for text nodes, the paragraph itself for pilcrow edits, and the
 * first descendant paragraph for table rows/cells (whose edits cover the
 * whole row/cell).
 */
function getNodeParagraphLocationId(editor: Editor, node: Node, path: number[]): string | null {
  if (isParagraphElement(node)) return node.locationId ?? null;

  if (Text.isText(node)) {
    const paragraphEntry = Editor.above<ParagraphElement>(editor, {
      at: path,
      match: isParagraphElement,
    });
    return paragraphEntry?.[0].locationId ?? null;
  }

  for (const [descendant] of Node.nodes(node)) {
    if (isParagraphElement(descendant)) return descendant.locationId ?? null;
  }

  return null;
}

function findParagraphByLocationId(editor: Editor, locationId: string) {
  return Editor.nodes<ParagraphElement>(editor, {
    at: [],
    match: node => isParagraphElement(node) && node.locationId === locationId,
  }).next().value;
}
